import { useFormContext } from "react-hook-form"
import { toast } from "sonner"
import emailSpellChecker from "@zootools/email-spell-checker"
import { Card, CardContent } from "@holigral/shared/ui/components/ui/card"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  ApiFormWrapper,
  FormError,
  FormInput,
  FormPasswordField,
  FormSubmitButton,
} from "@holigral/shared/form"
import { Link, useLocale } from "@holigral/shared/routing"
import { parseUnknownError } from "@holigral/shared/api"
import SocialLogin from "./SocialLogin"
import { useLoginMutation, useRegisterMutation } from "@/auth"
import { useDebounce } from "@/utils"
import { Trans, useT } from "@/i18n"

type Props = {
  email?: string
}

type AuthValues = {
  email: string
  password: string | null
}

const isUnauthorized = (error: unknown): boolean =>
  !!error &&
  typeof error === "object" &&
  "status" in error &&
  error.status === 401

const isEmailTaken = (error: unknown): boolean => {
  const violations = parseUnknownError<AuthValues>(error)

  return !!violations && violations.some((v) => v.propertyPath === "email")
}

/**
 * Single sign-in/sign-up surface:
 *
 * | Email exists? | Password?  | Outcome                                |
 * | ------------- | ---------- | -------------------------------------- |
 * | yes           | correct    | logged in                              |
 * | yes           | wrong      | error + inline sign-in link offer      |
 * | yes           | blank      | sign-in link emailed                   |
 * | no            | filled     | account created with it, logged in     |
 * | no            | blank      | pending account created, link emailed  |
 *
 * The inline offer goes through the same blank-password path, so it works
 * for both a forgotten password and a failed sign-up attempt.
 */
export default function AuthPage({ email }: Props) {
  const { t } = useT("auth")
  const [login, { error: loginError }] = useLoginMutation()
  const [register, { data: registerData, isSuccess: registerSuccess, reset }] =
    useRegisterMutation()

  // The page locale (from the /{locale}/ URL) seeds the new account's locale
  // and renders the legal links below.
  const locale = useLocale()

  // Blank-password submits always email a link (to the existing account, or
  // to a freshly created pending one), so this copy can be confident
  if (registerSuccess && !("token" in registerData)) {
    return <LinkSentCard onRetry={reset}>{t("emailLinkSent")}</LinkSentCard>
  }

  const continueAuth = (values: AuthValues) => ({
    unwrap: async (): Promise<unknown> => {
      const { email: address, password } = values

      if (!password) {
        return register({
          email: address,
          password: null,
          locale,
        }).unwrap()
      }

      try {
        return await login({ email: address, password }).unwrap()
      } catch (loginFailure) {
        if (!isUnauthorized(loginFailure)) {
          throw loginFailure
        }

        // No account matches — try creating one with these credentials
        try {
          const created = await register({
            email: address,
            password,
            locale,
          }).unwrap()
          toast.success(t("accountCreated", { email: address }), {
            duration: 10000,
          })

          return created
        } catch (registerFailure) {
          // The email is taken after all, so the password was simply wrong
          throw isEmailTaken(registerFailure) ? loginFailure : registerFailure
        }
      }
    },
  })

  return (
    <div>
      <h1 className="mb-6 text-center text-3xl font-normal tracking-tight">
        {t("welcome")}
      </h1>
      <div className="flex flex-col">
        <div className="mb-4">
          <SocialLogin />
        </div>
        <h4 className="mb-4 text-center">{t("divider")}</h4>
        <ApiFormWrapper
          defaultValues={{
            email: email ?? "",
            password: null,
          }}
          action={continueAuth}
          resetOnSuccess
        >
          <FormInput
            type="email"
            name="email"
            placeholder={t("emailPlaceholder")}
            required
            autoFocus
            className="mb-4 w-full"
          />
          <EmailTypoHint />
          <FormPasswordField
            name="password"
            placeholder={t("passwordPlaceholder")}
            isNewPassword={false}
            required={false}
            helperText={t("passwordHelper")}
            className="mb-4 w-full"
          />
          <FormError />
          {isUnauthorized(loginError) && (
            <LoginLinkOffer
              onSend={(address) => {
                void register({
                  email: address,
                  password: null,
                  locale,
                })
              }}
            />
          )}
          <FormSubmitButton className="w-full">
            {t("continue")}
          </FormSubmitButton>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="terms"
              components={{
                termsLink: (
                  <Link
                    to="/$locale/legal/$slug"
                    params={{ locale, slug: "terms" }}
                  />
                ),
                privacyLink: (
                  <Link
                    to="/$locale/legal/$slug"
                    params={{ locale, slug: "privacy" }}
                  />
                ),
              }}
            />
          </p>
        </ApiFormWrapper>
      </div>
    </div>
  )
}

function LinkSentCard({
  onRetry,
  children,
}: {
  onRetry: () => void
  children: string
}) {
  const { t } = useT("auth")

  return (
    <div>
      <h1 className="mb-6 text-center text-3xl font-normal tracking-tight">
        {t("checkEmailTitle")}
      </h1>
      <Card>
        <CardContent>
          <p>{children}</p>
          <p>
            <Trans
              t={t}
              i18nKey="spamHint"
              components={{ retryLink: <Link to="." onClick={onRetry} /> }}
            />
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function LoginLinkOffer({ onSend }: { onSend: (email: string) => void }) {
  const { t } = useT("auth")
  const { getValues } = useFormContext<AuthValues>()

  return (
    <p className="mb-4 text-center">
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          onSend(getValues("email"))
        }}
      >
        {t("loginLinkOffer")}
      </Button>
    </p>
  )
}

function EmailTypoHint() {
  const { t } = useT("auth")
  const { watch, setValue } = useFormContext<AuthValues>()
  const email = useDebounce(watch("email"), { delay: "long" })

  const suggestion = email.includes("@")
    ? emailSpellChecker.run({ email })
    : undefined

  if (!suggestion) {
    return null
  }

  return (
    <p className="-mt-2 mb-4 text-sm text-muted-foreground">
      <Trans
        t={t}
        i18nKey="didYouMean"
        values={{ suggestion: suggestion.full }}
        components={{
          suggestionButton: (
            <Button
              type="button"
              variant="ghost"
              className="h-auto p-0"
              onClick={() => {
                setValue("email", suggestion.full, { shouldDirty: true })
              }}
            />
          ),
        }}
      />
    </p>
  )
}
